/**
 * CompletionScreen — Final screen
 *
 * Two-column layout:
 *  LEFT  — Setup Summary (one row per step, status colour, Edit ↗ link)
 *  RIGHT — What to do next + primary CTA + secondary link
 */

import { CheckCircle2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useNavigate } from 'react-router-dom';
import { WIZARD_STEPS } from './wizardSteps';
import type { StepStatus } from './WizardShell';

interface CompletionScreenProps {
  stepStatuses: Record<number, StepStatus>;
  onFinish: () => void;
}

const NEXT_ACTIONS = [
  'Create your first project and assign a project manager.',
  'Invite your team members and assign their platform roles.',
  'Review the dashboard to see your portfolio at a glance.',
  'Run a WIP report to understand your current work in progress.',
  'Set up your first collection strategy in billing.',
];

const STATUS_STYLES: Record<StepStatus, string> = {
  completed: 'text-emerald-600',
  skipped: 'text-warning-500',
  pending: 'text-muted-foreground',
};

const STATUS_LABELS: Record<StepStatus, string> = {
  completed: 'Completed',
  skipped: 'Skipped',
  pending: 'Not configured',
};

const STATUS_DOT: Record<StepStatus, string> = {
  completed: 'bg-emerald-500',
  skipped: 'bg-warning-400',
  pending: 'bg-muted-foreground/40',
};

export function CompletionScreen({ stepStatuses, onFinish }: CompletionScreenProps) {
  const navigate = useNavigate();
  const { completeOnboarding } = useOnboarding();

  const handleCTA = (route: string) => {
    completeOnboarding();
    navigate(route);
  };

  return (
    <div className="min-h-full px-8 py-10">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">You're all set!</h2>
          <p className="text-muted-foreground max-w-md">
            Access Projects is configured and ready to use. Here's a summary of
            what you set up.
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* LEFT — Setup Summary */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Setup Summary
            </h3>
            <div className="rounded-lg border border-border divide-y divide-border">
              {WIZARD_STEPS.map((step) => {
                const status = stepStatuses[step.id] ?? 'pending';
                return (
                  <div
                    key={step.id}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={cn(
                          'h-2 w-2 rounded-full flex-shrink-0',
                          STATUS_DOT[status],
                        )}
                      />
                      <span className="text-sm font-medium text-foreground">
                        {step.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={cn('text-xs font-medium', STATUS_STYLES[status])}
                      >
                        {STATUS_LABELS[status]}
                      </span>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                        onClick={() => handleCTA(step.route)}
                      >
                        Edit
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT — What to do next */}
          <div className="space-y-5">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                What to do next
              </h3>
              <ol className="space-y-2">
                {NEXT_ACTIONS.map((action, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-foreground">
                    <span className="h-5 w-5 flex-shrink-0 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center mt-0.5">
                      {i + 1}
                    </span>
                    {action}
                  </li>
                ))}
              </ol>
            </div>

            {/* CTAs */}
            <div className="space-y-3 pt-2">
              <Button
                className="w-full"
                size="lg"
                onClick={() => handleCTA('/projects')}
              >
                Go to Projects →
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleCTA('/dashboard')}
              >
                Go to Dashboard
              </Button>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              You can always restart this setup from the Settings menu in the sidebar.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
